import KawaseBlurFilterPipelinePlugin from "phaser3-rex-plugins/plugins/kawaseblurpipeline-plugin";
import ResizableScene from "../core/ResizableScene";
import ActionQueue from "../utils/ActionQueue";
import { StoryImageContainer } from "./StoryImageContainer";

enum StoryAtlas {
    Story1 = "storyAtlas1",
    Story2 = "storyAtlas2",
    Story3 = "storyAtlas3",
}

export default class FilmReelManager extends Phaser.GameObjects.Container {
    reels: StoryImageContainer[] = [];
    frames: Phaser.GameObjects.Image[] = [];
    slope = new Phaser.Math.Vector2(79, 500);
    currentFrameIndex: number = 0;

    private _kawasePipelinePlugin: KawaseBlurFilterPipelinePlugin;

    constructor(scene:ResizableScene) {
        super(scene, 0, 0)

        for(let i = 0; i < 3; i++) {
            const reel = new StoryImageContainer(scene);
            this.add(reel);
            this.reels.push(reel);

            for(const frame of reel.storyImages) {
                this.frames.push(frame);
            }
        }
        const reelWidth = this.reels[0].displayWidth;
        const reelHeight = this.reels[0].displayHeight;

        const offset = 165;
        this.reels[0].y -= reelHeight - offset;
        this.reels[2].y += reelHeight - offset;

        this.reels[0].setStoryImages(StoryAtlas.Story2);

        this.setSize(reelWidth, reelHeight * 3 - offset * 2);
        this.rotation = -this.slope.x / this.slope.y;

        this._kawasePipelinePlugin = scene.plugins.get('rexKawaseBlurPipeline') as KawaseBlurFilterPipelinePlugin;
        this.currentFrameIndex = Math.floor(this.frames.length / 2);
    }

    switchTo(index: number): void {
        let atlas = StoryAtlas.Story1;
        switch(index){
            case 1: {
                atlas = StoryAtlas.Story2;
                break;
            }
            case 2: {
                atlas = StoryAtlas.Story3;
                break;
            }
        }
		const queue = new ActionQueue();
        queue.add(complete =>{
            // add blur shader with baseline properties
            const blurFX = this._kawasePipelinePlugin.add(this, {
                blur: 0.001, // animate to 10.0
                quality: 5,
                pixelWidth: 1,
                pixelHeight: 5,
                name: 'rexKawaseBlurPostFx'
            });

            const animDuration = 200;
            this.scene.tweens.add({
                targets: this,
                alpha: 1,
                duration: animDuration,
                ease: Phaser.Math.Easing.Quadratic.InOut,
                onUpdate: (tween: Phaser.Tweens.Tween)=> {
                    // animate blur
                    let blurProgress = tween.progress / 0.55;
                    if(tween.progress > 0.5) blurProgress = (1 / tween.progress) - 1;
                    blurFX.setBlur((10.0 * blurProgress) + 0.001)
                }
            })
            // make sure we are at the bottom reel mid frame before starting anim
            this.goToFrame(12); 
            this.goToFrame(2, animDuration, ()=> {
                this._kawasePipelinePlugin.remove(this);
                this.setStoryImages(atlas)
                this.goToFrame(12);
                complete();
            });
        });
        queue.start();
    }

    setBlur(): void {
        this._kawasePipelinePlugin.add(this, {
            blur: 6,
            quality: 5,
            pixelWidth: 1,
            pixelHeight: 5,

            name: 'rexKawaseBlurPostFx'
        });
    }

    setStoryImages(atlas: StoryAtlas): void {
        switch(atlas) {
            case StoryAtlas.Story2:
                this.reels[0].setStoryImages(StoryAtlas.Story3);
                this.reels[1].setStoryImages(StoryAtlas.Story2);
                this.reels[2].setStoryImages(StoryAtlas.Story2);
                break;
            case StoryAtlas.Story3:
                this.reels[0].setStoryImages(StoryAtlas.Story1);
                this.reels[1].setStoryImages(StoryAtlas.Story3);
                this.reels[2].setStoryImages(StoryAtlas.Story3);
                break;
            default:
                this.reels[0].setStoryImages(StoryAtlas.Story2);
                this.reels[1].setStoryImages(StoryAtlas.Story1);
                this.reels[2].setStoryImages(StoryAtlas.Story1);
                break;
        }
    }

    goToFrame(frameIndex: number, duration = 0, onComplete?: ()=> void): void {
        if (frameIndex < 0 || frameIndex > this.frames.length - 1) return;

        const nextFrame = this.frames[frameIndex].getWorldTransformMatrix();
        const curFrame = this.frames[this.currentFrameIndex].getWorldTransformMatrix();

        const xDelta = curFrame.tx - nextFrame.tx;
        const yDelta = curFrame.ty - nextFrame.ty;

        if (duration == 0) {
            this.x += xDelta;
            this.y += yDelta;
        }
        else {
            this.scene.tweens.add({
                targets: this,
                x: { from: this.x, to: this.x + xDelta},
                y: {from: this.y, to: this.y + yDelta},
                duration: duration,
                ease: Phaser.Math.Easing.Quadratic.InOut,
                onComplete: ()=> {
                    if(onComplete) onComplete();
                }
            })
        }  
        this.currentFrameIndex = frameIndex;
    }
}
